import type { 
  User, UserProfile, Form, Order, Product, Category,
  DashboardStats, AnalyticsData, SystemSettings,
  ApiResponse, PaginatedResponse, UploadedFile,
  SearchFilters, PaginationParams
} from '../types'

const API_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8787/api'

interface CacheData {
  data: unknown
  timestamp: number
}

interface FormAnalytics {
  views: number
  submissions: number
  conversionRate: number
}

interface Notification {
  id: number
  title: string
  message: string
  read: boolean
  created_at: string
}

export class ApiService {
  private token: string | null = null

  constructor() {
    this.token = localStorage.getItem('token')
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem('token', token)
  }

  removeToken() {
    this.token = null
    localStorage.removeItem('token')
  }

  private cache: Map<string, CacheData> = new Map()
  private cacheExpiry = 5 * 60 * 1000

  private getCacheKey(endpoint: string, options: RequestInit = {}): string {
    if (options.method && options.method !== 'GET') {
      return ''
    }
    return `${endpoint}_${JSON.stringify(options)}`
  }

  private getFromCache(key: string): unknown {
    if (!key) return null
    const cached = this.cache.get(key)
    if (cached) {
      const now = Date.now()
      if (now - cached.timestamp < this.cacheExpiry) {
        return cached.data
      } else {
        this.cache.delete(key)
      }
    }
    return null
  }

  private setToCache(key: string, data: unknown): void {
    if (!key) return
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    })
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const cacheKey = this.getCacheKey(endpoint, options)
      const cachedData = this.getFromCache(cacheKey) as ApiResponse<T> | null
      if (cachedData) {
        return cachedData
      }

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`
      }
      
      const url = `${API_URL}${endpoint}`
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...(options.headers as Record<string, string> || {}),
        },
      })
      
      let data: Record<string, unknown>
      try {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          data = await response.json()
        } else {
          await response.text()
          return { error: `Server returned non-JSON response: ${response.status}` }
        }
      } catch {
        return { error: `Failed to parse server response: ${response.status}` }
      }
      
      if (!response.ok) {
        return { error: (data?.error || data?.message || `Request failed: ${response.status}`) as string, errors: data?.errors as Record<string, string[]> }
      }
      
      const result = data.data !== undefined ? { data: data.data as T } : { data: data as T }
      
      this.setToCache(cacheKey, result)
      
      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      return { error: `Network error: ${errorMessage}` }
    }
  }

  async register(name: string, email: string, password: string, company?: string, phone?: string) {
    return this.request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password, company, phone }),
    })
  }

  async login(email: string, password: string) {
    return this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async logout() {
    return this.request<{ message: string }>('/auth/logout', {
      method: 'POST',
    })
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    })
  }

  async getProfile() {
    return this.request<{ user: UserProfile }>('/users/me')
  }

  async updateProfile(name: string, company?: string, phone?: string, avatar?: string) {
    return this.request<{ user: User }>('/users/me', {
      method: 'PUT',
      body: JSON.stringify({ name, company, phone, avatar }),
    })
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/users/me/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  }

  async deleteAccount() {
    return this.request<{ message: string }>('/users/me', {
      method: 'DELETE',
    })
  }

  async getUsers(pagination?: PaginationParams) {
    const params = new URLSearchParams()
    if (pagination?.page) params.append('page', pagination.page.toString())
    if (pagination?.limit) params.append('limit', pagination.limit.toString())
    
    const queryString = params.toString()
    return this.request<PaginatedResponse<User>>(`/users${queryString ? `?${queryString}` : ''}`)
  }

  async getUserById(id: string | number) {
    return this.request<{ user: User }>(`/users/${id}`)
  }

  async updateUser(id: string | number, userData: Partial<User>) {
    return this.request<{ user: User }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    })
  }

  async deleteUser(id: string | number) {
    return this.request<{ message: string }>(`/users/${id}`, {
      method: 'DELETE',
    })
  }

  async createUser(userData: Partial<User>) {
    return this.request<{ user: User }>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async getForms(filters?: SearchFilters, pagination?: PaginationParams) {
    const params = new URLSearchParams()
    if (filters?.query) params.append('query', filters.query)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters?.dateTo) params.append('dateTo', filters.dateTo)
    if (filters?.sortBy) params.append('sortBy', filters.sortBy)
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)
    if (pagination?.page) params.append('page', pagination.page.toString())
    if (pagination?.limit) params.append('limit', pagination.limit.toString())
    
    const queryString = params.toString()
    return this.request<PaginatedResponse<Form>>(`/forms${queryString ? `?${queryString}` : ''}`)
  }

  async createForm(formData: Partial<Form>) {
    return this.request<{ form: Form }>('/forms', {
      method: 'POST',
      body: JSON.stringify(formData),
    })
  }

  async getForm(id: string | number) {
    return this.request<{ form: Form }>(`/forms/${id}`)
  }

  async getFormById(id: string | number) {
    return this.request<{ form: Form }>(`/forms/${id}`)
  }

  async updateForm(id: string | number, formData: Partial<Form>) {
    return this.request<{ form: Form }>(`/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(formData),
    })
  }

  async deleteForm(id: string | number) {
    return this.request<{ message: string }>(`/forms/${id}`, {
      method: 'DELETE',
    })
  }

  async duplicateForm(id: string | number) {
    return this.request<{ form: Form }>(`/forms/${id}/duplicate`, {
      method: 'POST',
    })
  }

  async publishForm(id: string | number) {
    return this.request<{ form: Form }>(`/forms/${id}/publish`, {
      method: 'POST',
    })
  }

  async unpublishForm(id: string | number) {
    return this.request<{ form: Form }>(`/forms/${id}/unpublish`, {
      method: 'POST',
    })
  }

  async getFormSubmissions(formId: string | number, pagination?: PaginationParams) {
    const params = new URLSearchParams()
    if (pagination?.page) params.append('page', pagination.page.toString())
    if (pagination?.limit) params.append('limit', pagination.limit.toString())
    
    const queryString = params.toString()
    return this.request<PaginatedResponse<Order>>(`/forms/${formId}/submissions${queryString ? `?${queryString}` : ''}`)
  }

  async getOrders(filters?: SearchFilters, pagination?: PaginationParams) {
    const params = new URLSearchParams()
    if (filters?.query) params.append('query', filters.query)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters?.dateTo) params.append('dateTo', filters.dateTo)
    if (filters?.sortBy) params.append('sortBy', filters.sortBy)
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder)
    if (pagination?.page) params.append('page', pagination.page.toString())
    if (pagination?.limit) params.append('limit', pagination.limit.toString())
    
    const queryString = params.toString()
    return this.request<PaginatedResponse<Order>>(`/orders${queryString ? `?${queryString}` : ''}`)
  }

  async createOrder(orderData: Partial<Order>) {
    return this.request<{ order: Order }>('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    })
  }

  async getOrder(id: string | number) {
    return this.request<{ order: Order }>(`/orders/${id}`)
  }

  async getOrderById(id: string | number) {
    return this.request<{ order: Order }>(`/orders/${id}`)
  }

  async updateOrder(id: string | number, orderData: Partial<Order>) {
    return this.request<{ order: Order }>(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    })
  }

  async updateOrderStatus(id: string | number, status: string, notes?: string) {
    return this.request<{ order: Order }>(`/orders/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    })
  }

  async deleteOrder(id: string | number) {
    return this.request<{ message: string }>(`/orders/${id}`, {
      method: 'DELETE',
    })
  }

  async bulkDeleteOrders(ids: number[]) {
    return this.request<{ message: string }>('/orders/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
  }

  async bulkUpdateOrderStatus(ids: number[], status: string) {
    return this.request<{ message: string }>('/orders/bulk-update-status', {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    })
  }

  async exportOrders(format: 'csv' | 'excel' | 'pdf' = 'csv', filters?: SearchFilters) {
    const params = new URLSearchParams()
    params.append('format', format)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters?.dateTo) params.append('dateTo', filters.dateTo)
    
    return this.request<{ downloadUrl: string }>(`/orders/export?${params.toString()}`)
  }

  async getProducts(filters?: SearchFilters, pagination?: PaginationParams) {
    const params = new URLSearchParams()
    if (filters?.query) params.append('query', filters.query)
    if (filters?.status) params.append('status', filters.status)
    if (pagination?.page) params.append('page', pagination.page.toString())
    if (pagination?.limit) params.append('limit', pagination.limit.toString())
    
    const queryString = params.toString()
    return this.request<PaginatedResponse<Product>>(`/products${queryString ? `?${queryString}` : ''}`)
  }

  async createProduct(productData: Partial<Product>) {
    return this.request<{ product: Product }>('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    })
  }

  async getProduct(id: string | number) {
    return this.request<{ product: Product }>(`/products/${id}`)
  }

  async getProductById(id: string | number) {
    return this.request<{ product: Product }>(`/products/${id}`)
  }

  async updateProduct(id: string | number, productData: Partial<Product>) {
    return this.request<{ product: Product }>(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    })
  }

  async deleteProduct(id: string | number) {
    return this.request<{ message: string }>(`/products/${id}`, {
      method: 'DELETE',
    })
  }

  async bulkDeleteProducts(ids: number[]) {
    return this.request<{ message: string }>('/products/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
  }

  async updateProductStock(id: string | number, stock: number) {
    return this.request<{ product: Product }>(`/products/${id}/stock`, {
      method: 'PUT',
      body: JSON.stringify({ stock }),
    })
  }

  async getCategories() {
    return this.request<{ categories: Category[] }>('/categories')
  }

  async createCategory(name: string, parentId?: number, description?: string) {
    return this.request<{ category: Category }>('/categories', {
      method: 'POST',
      body: JSON.stringify({ name, parent_id: parentId, description }),
    })
  }

  async updateCategory(id: string | number, data: Partial<Category>) {
    return this.request<{ category: Category }>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteCategory(id: string | number) {
    return this.request<{ message: string }>(`/categories/${id}`, {
      method: 'DELETE',
    })
  }

  async getDashboardStats() {
    return this.request<{ stats: DashboardStats }>('/stats/dashboard')
  }

  async getAnalytics(period: '7days' | '30days' | '90days' | 'year' = '30days') {
    return this.request<{ analytics: AnalyticsData }>(`/stats/analytics?period=${period}`)
  }

  async getFormAnalytics(formId: string | number, period?: string) {
    const params = new URLSearchParams()
    if (period) params.append('period', period)
    return this.request<{ analytics: FormAnalytics }>(`/stats/forms/${formId}/analytics?${params.toString()}`)
  }

  async uploadFile(file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<UploadedFile>> {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const xhr = new XMLHttpRequest()
      
      return new Promise((resolve) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100)
            onProgress(progress)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: JSON.parse(xhr.responseText) })
          } else {
            resolve({ error: 'Upload failed' })
          }
        })

        xhr.addEventListener('error', () => {
          resolve({ error: 'Upload failed' })
        })

        xhr.open('POST', `${API_URL}/upload`)
        if (this.token) {
          xhr.setRequestHeader('Authorization', `Bearer ${this.token}`)
        }
        xhr.send(formData)
      })
    } catch {
      return { error: 'Upload failed' }
    }
  }

  async deleteFile(fileId: string) {
    return this.request<{ message: string }>(`/upload/${fileId}`, {
      method: 'DELETE',
    })
  }

  async getSystemSettings() {
    return this.request<{ settings: SystemSettings }>('/settings')
  }

  async updateSystemSettings(settings: Partial<SystemSettings>) {
    return this.request<{ settings: SystemSettings }>('/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    })
  }

  async healthCheck() {
    return this.request<{ status: string; timestamp: string }>('/health')
  }

  async getNotifications() {
    return this.request<{ notifications: Notification[] }>('/notifications')
  }

  async markNotificationAsRead(id: number) {
    return this.request<{ message: string }>(`/notifications/${id}/read`, {
      method: 'PUT',
    })
  }

  async markAllNotificationsAsRead() {
    return this.request<{ message: string }>('/notifications/read-all', {
      method: 'PUT',
    })
  }

  async deleteNotification(id: number) {
    return this.request<{ message: string }>(`/notifications/${id}`, {
      method: 'DELETE',
    })
  }

  async exportData(type: 'forms' | 'orders' | 'products', format: 'csv' | 'excel' | 'json' = 'csv', filters?: SearchFilters) {
    const params = new URLSearchParams()
    params.append('format', format)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
    if (filters?.dateTo) params.append('dateTo', filters.dateTo)
    
    return this.request<{ downloadUrl: string }>(`/${type}/export?${params.toString()}`)
  }

  async importData(type: 'forms' | 'orders' | 'products', file: File) {
    const formData = new FormData()
    formData.append('file', file)
    
    try {
      const headers: Record<string, string> = {}
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`
      }

      const response = await fetch(`${API_URL}/${type}/import`, {
        method: 'POST',
        headers,
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        return { error: data.error || 'Import failed', errors: data.errors }
      }

      return { data }
    } catch {
      return { error: 'Import failed' }
    }
  }
}

export const apiService = new ApiService()
